import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { ActiveHighlightRow, CaptureDestinationRow, HighlightTypeRow } from '@flow/sync';

import type { LocalDatabase } from '../db/powersync.js';
import { useSetDetail } from '../shell/AppLayout.jsx';

import { TaskDetail } from './TaskDetail.jsx';
import { useDetailLayout } from './use-detail-layout.js';
import {
  useTaskActions,
  type TaskActions,
  type TaskBlock,
  type TaskChild,
} from './use-task-actions.jsx';

/**
 * The detail panel and the task actions of a screen (f1-detail-panel D7, f1-trash-search D6): the
 * list views and the search page mount the same panel, in the shell's slot, with the same actions.
 * The screen hands over what it already has (its rows); the panel registers what only it shows (a
 * subtask, a block that left the view, a search result).
 */

export interface DetailHostInput {
  userId: string;
  local: LocalDatabase;
  workspaceId: string;
  stages: CaptureDestinationRow[];
  period: { id: string } | undefined;
  type: HighlightTypeRow | undefined;
  active: ActiveHighlightRow[];
  highlightsEnabled: boolean;
  today: string;
  openTaskId: string | null;
  onOpenTask: ((id: string | null) => void) | undefined;
  /** Blocks and children the screen already has. */
  listBlock?: (id: string) => TaskBlock | undefined;
  listChildren?: (id: string) => TaskChild[];
  onAct?: (id: string) => void;
  onCompletedHere?: (id: string, completed: boolean) => void;
  /** After the panel closes: the screen puts the focus back where it was. */
  onClosed?: () => void;
}

export function useDetailHost(input: DetailHostInput): TaskActions {
  const setDetail = useSetDetail();
  const layout = useDetailLayout();
  const { userId, local, workspaceId, stages, type, active, highlightsEnabled, today } = input;
  const { openTaskId, onOpenTask } = input;

  const fromDetail = useRef<{
    blocks: Map<string, TaskBlock>;
    children: Record<string, TaskChild[]>;
  }>({ blocks: new Map(), children: {} });
  const register = useCallback((blocks: TaskBlock[], kids: Record<string, TaskChild[]>) => {
    fromDetail.current = {
      blocks: new Map(blocks.map((block) => [block.id, block])),
      children: kids,
    };
  }, []);

  // The actions also reach what only the panel has; they run from handlers, never during render.
  const actions = useTaskActions({
    local,
    userId,
    workspaceId,
    stages,
    period: input.period,
    type,
    active,
    highlightsEnabled,
    today,
    blockOf: (id) => input.listBlock?.(id) ?? fromDetail.current.blocks.get(id),
    childrenOf: (id) => {
      const listed = input.listChildren?.(id) ?? [];
      return listed.length > 0 ? listed : (fromDetail.current.children[id] ?? []);
    },
    ...(input.onAct ? { onAct: input.onAct } : {}),
    ...(input.onCompletedHere ? { onCompletedHere: input.onCompletedHere } : {}),
  });

  const closed = useRef(input.onClosed);
  useEffect(() => {
    closed.current = input.onClosed;
  });
  const highlighted = useMemo(() => new Set(active.map((h) => h.block_id)), [active]);

  // The panel lives in the shell's own slot (docs/05 §5.1).
  const detailNode = useMemo(
    () =>
      openTaskId && workspaceId ? (
        <TaskDetail
          key={openTaskId}
          blockId={openTaskId}
          userId={userId}
          local={local}
          workspaceId={workspaceId}
          layout={layout}
          stages={stages}
          highlightType={highlightsEnabled ? type : undefined}
          highlighted={highlighted.has(openTaskId)}
          highlightsUsed={active.length}
          actions={actions}
          today={today}
          onOpen={(id) => onOpenTask?.(id)}
          onClose={() => {
            onOpenTask?.(null);
            closed.current?.();
          }}
          onGone={() => onOpenTask?.(null)}
          register={register}
        />
      ) : null,
    // The actions change on every write; the panel reads its own data.
    [
      openTaskId,
      workspaceId,
      userId,
      local,
      layout,
      stages,
      highlightsEnabled,
      type,
      highlighted,
      active,
      actions,
      today,
      onOpenTask,
      register,
    ],
  );
  useEffect(() => {
    setDetail(detailNode);
    return () => setDetail(null);
  }, [detailNode, setDetail]);

  return actions;
}
